import { useState, useEffect, useMemo } from 'react';

import { isEmpty, isNonNullish } from 'remeda';

import { Box, CircularProgress } from '@mui/material';

import { RewardModel } from '@shared/types';

import {
  useBulkDeleteRewardsMutation,
  useCreateRewardMutation,
  useGetReferralRewardSettingsQuery,
  useLazyGetRewardsByIdsQuery,
  useUpdateReferralRewardSettingsMutation,
  useUpdateRewardMutation,
} from '@services';

import { getChangedRewardsInfo } from '../../../../shared/utils';
import { REFERRAL_REWARD_SETTINGS_DEFAULT_VALUES } from '../constants';
import { ReferralValidationForm, ReferralValidationFormValues } from '../ReferralValidationForm';

import { getReferralValidationEditFormInitialValues, getReferralValidationModalFromFormValues } from './utils';

import styles from '../ReferralRewardSettingsPage.module.css';

const { settingsContainer } = styles;

export const ReferralValidationSettings = () => {
  const [initialReferralRewards, setInitialReferralRewards] = useState<RewardModel[]>([]);
  const [initialInviterRewards, setInitialInviterRewards] = useState<RewardModel[]>([]);

  const { data: referralRewardSettings, isLoading: isSettingsLoading } = useGetReferralRewardSettingsQuery();

  const [updateReferralRewardSettings, { isLoading: isSettingsUpdating }] = useUpdateReferralRewardSettingsMutation();

  const [getRewardsByIds] = useLazyGetRewardsByIdsQuery();
  const [bulkDeleteRewards] = useBulkDeleteRewardsMutation();
  const [createReward] = useCreateRewardMutation();
  const [updateReward] = useUpdateRewardMutation();

  useEffect(() => {
    if (referralRewardSettings === undefined) {
      return;
    }

    const {
      referralValidationRewards: { referralRewardIds, inviterRewardIds },
    } = referralRewardSettings;

    const getInitialRewards = async () => {
      const [currentReferralRewards, currentInviterRewards] = await Promise.all([
        Boolean(referralRewardIds.length) && getRewardsByIds(referralRewardIds).unwrap(),
        Boolean(inviterRewardIds.length) && getRewardsByIds(inviterRewardIds).unwrap(),
      ]);

      setInitialReferralRewards(currentReferralRewards || []);
      setInitialInviterRewards(currentInviterRewards || []);
    };

    getInitialRewards();
  }, [referralRewardSettings]);

  const handleSubmit = async (formValues: ReferralValidationFormValues) => {
    const { inviterRewards, referralRewards, requiredTasksForValidate, isActive } = formValues;
    const {
      deletedRewardIds: deletedReferralRewardIds,
      createdRewardItems: createdReferralRewardItems,
      updatedRewardItems: updatedReferralRewardItems,
      restRewardItems: restReferralRewardItems,
    } = getChangedRewardsInfo(initialReferralRewards, referralRewards);
    const {
      deletedRewardIds: deletedInviterRewardIds,
      createdRewardItems: createdInviterRewardItems,
      updatedRewardItems: updatedInviterRewardItems,
      restRewardItems: restInviterRewardItems,
    } = getChangedRewardsInfo(initialInviterRewards, inviterRewards);

    const isDeletedReferralRewardsExisting = deletedReferralRewardIds.length !== 0;
    const isDeletedInviterRewardsExisting = deletedInviterRewardIds.length !== 0;

    if (isDeletedReferralRewardsExisting) {
      await bulkDeleteRewards(deletedReferralRewardIds);
    }

    await Promise.all([
      isDeletedReferralRewardsExisting && bulkDeleteRewards(deletedReferralRewardIds),
      isDeletedInviterRewardsExisting && bulkDeleteRewards(deletedInviterRewardIds),
    ]);

    let createdReferralRewards: RewardModel[] = [];
    let createdInviterRewards: RewardModel[] = [];
    let updatedReferralRewards: RewardModel[] = [];
    let updatedInviterRewards: RewardModel[] = [];

    const isCreatedReferralRewardsExisting = createdReferralRewardItems.length !== 0;
    const isCreatedInviterRewardsExisting = createdInviterRewardItems.length !== 0;

    if (isCreatedReferralRewardsExisting) {
      createdReferralRewards = await Promise.all(
        createdReferralRewardItems.map(async (reward) => {
          const { id, type, value } = await createReward(reward).unwrap();

          return new RewardModel(id, type, value);
        })
      );
    }

    if (isCreatedInviterRewardsExisting) {
      createdInviterRewards = await Promise.all(
        createdInviterRewardItems.map(async (reward) => {
          const { id, type, value } = await createReward(reward).unwrap();

          return new RewardModel(id, type, value);
        })
      );
    }

    const isUpdatedReferralRewardsExisting = updatedReferralRewardItems.length !== 0;
    const isUpdatedInviterRewardsExisting = updatedInviterRewardItems.length !== 0;

    if (isUpdatedReferralRewardsExisting) {
      updatedReferralRewards = await Promise.all(
        updatedReferralRewardItems.map(async ({ id, ...rewardParams }) => {
          const { type, value } = await updateReward({
            rewardId: id,
            payload: rewardParams,
          }).unwrap();
          return new RewardModel(id, type, value);
        })
      );
    }

    if (isUpdatedInviterRewardsExisting) {
      updatedInviterRewards = await Promise.all(
        updatedInviterRewardItems.map(async ({ id, ...rewardParams }) => {
          const { type, value } = await updateReward({
            rewardId: id,
            payload: rewardParams,
          }).unwrap();
          return new RewardModel(id, type, value);
        })
      );
    }

    await updateReferralRewardSettings(
      getReferralValidationModalFromFormValues(
        requiredTasksForValidate,
        isActive,
        referralRewardSettings!,
        [...createdReferralRewards, ...updatedReferralRewards, ...restReferralRewardItems],
        [...createdInviterRewards, ...updatedInviterRewards, ...restInviterRewardItems]
      )
    ).unwrap();
  };

  const initialValues = useMemo(
    () =>
      isNonNullish(referralRewardSettings) && !isEmpty(referralRewardSettings)
        ? getReferralValidationEditFormInitialValues(
            referralRewardSettings!,
            initialReferralRewards,
            initialInviterRewards
          )
        : REFERRAL_REWARD_SETTINGS_DEFAULT_VALUES,
    [initialReferralRewards, initialInviterRewards, referralRewardSettings]
  );

  if (isSettingsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={settingsContainer}>
      <ReferralValidationForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isSubmitting={isSettingsUpdating}
        submitButtonCaption="Update"
      />
    </Box>
  );
};
